/**
 * Wizard form state for multi-country scheduled sent edit (drop options, static tag, propagation, creative).
 */

import { useCallback, useMemo, useState } from 'react';

import { useShortlinkOptionsState } from '@/features/smswiser-campaigns/hooks/useShortlinkOptionsState';
import { useMultiCountryCreativeState } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks/useMultiCountryCreativeState';
import type { WizardStep } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks/useMultiCountryBulkEditForm';
import type {
  PerCountryOverride,
  TriState,
} from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/multiCountryBulkEdit.types';

export interface UseMultiCountrySentScheduledEditFormParams {
  selectedCountryKeys: string[];
}

export function useMultiCountrySentScheduledEditForm(params: UseMultiCountrySentScheduledEditFormParams) {
  const { selectedCountryKeys } = params;
  const creative = useMultiCountryCreativeState();

  const [step, setStep] = useState<WizardStep>(1);
  const goNext = useCallback(() => setStep((s) => Math.min(s + 1, 3) as WizardStep), []);
  const goBack = useCallback(() => setStep((s) => Math.max(s - 1, 1) as WizardStep), []);

  const [forceSend, setForceSend] = useState<TriState<boolean>>('unchanged');
  const [staticTagId, setStaticTagId] = useState<TriState<string | null>>('unchanged');
  const shortlink = useShortlinkOptionsState();
  const [overrides, setOverrides] = useState<Record<string, PerCountryOverride>>({});
  const [propagateToCountries, setPropagateToCountries] = useState<string[]>([]);

  const setCountryOverride = useCallback((ck: string, patch: Partial<PerCountryOverride>) => {
    setOverrides((prev) => ({ ...prev, [ck]: { ...prev[ck], ...patch } }));
  }, []);

  const allCountryKeys = useMemo(() => {
    const combined = new Set([...selectedCountryKeys, ...propagateToCountries]);
    return [...combined].sort();
  }, [selectedCountryKeys, propagateToCountries]);

  const hasPerCountryCreative = useMemo(
    () =>
      Object.values(creative.perCountryTexts).some((v) => v.length > 0) ||
      Object.values(creative.perCountrySenders).some((v) => v.length > 0),
    [creative.perCountryTexts, creative.perCountrySenders],
  );

  const hasAnyChange = useMemo(
    () =>
      (forceSend !== 'unchanged' && forceSend !== 'mixed') ||
      shortlink.picked ||
      (staticTagId !== 'unchanged' && staticTagId !== 'mixed') ||
      creative.selectedSenderNames.length > 0 ||
      creative.selectedSmsTexts.length > 0 ||
      creative.perDropSmsTexts != null ||
      creative.perDropSenderNames != null ||
      hasPerCountryCreative ||
      Object.keys(overrides).length > 0 ||
      propagateToCountries.length > 0,
    [
      forceSend,
      shortlink.picked,
      staticTagId,
      creative,
      hasPerCountryCreative,
      overrides,
      propagateToCountries.length,
    ],
  );

  return {
    step,
    setStep,
    goNext,
    goBack,
    selectedCountryKeys,
    allCountryKeys,
    forceSend,
    setForceSend,
    /** Editable on sent drops too — SMSWiser's PATCH accepts both options. */
    shortlink,
    staticTagId,
    setStaticTagId,
    overrides,
    setCountryOverride,
    propagateToCountries,
    setPropagateToCountries,
    ...creative,
    hasAnyChange,
  };
}
